import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import Reveal, { LineReveal, EASE } from "./Reveal";
import EmailCapture from "./EmailCapture";

const BIO = [
    "Mashi is a Sri Lankan American singer, songwriter, and performer based in Chicago, whose sound lives at the intersection of Neo-Soul, Jazz, R&B, and Soulful Blues, with House now joining the mix. She has been singing since before she could speak and taught herself piano by ear.",
    "Inspired by the musical cadence of the Sinhala language and shaped by her years as a competitive athlete, Mashi brings discipline and resolve to the stage. Her music is rooted in emotional honesty, turning love, heartbreak, identity, and resilience into songs that invite listeners to recognize the power in their own comebacks.",
    "Now, she is stepping into the spotlight with her debut recording alongside Chicago House pioneer Vince Lawrence, the start of a versatile career that shines brightest live. Her ambitions are big: to perform, to travel, and one day to sell out stadiums.",
];

export default function About() {
    const [bioOpen, setBioOpen] = useState(false);

    return (
        <section id="about" className="scroll-mt-20 bg-blush text-garden">
            <div className="px-5 md:px-10 py-24 md:py-36 max-w-[1600px] mx-auto w-full grid md:grid-cols-12 gap-12 md:gap-8 items-start">
                <div className="md:col-span-7 order-2 md:order-1">
                    <p className="text-[11px] md:text-xs uppercase tracking-[0.25em] text-rose mb-6 md:mb-10 flex items-center gap-3">
                        <span className="h-2 w-2 bg-olive" aria-hidden="true" />
                        01 — About
                    </p>

                    <h2 className="font-display font-bold leading-[1.08] tracking-tight text-3xl md:text-5xl lg:text-[3.4rem] text-garden">
                        <LineReveal>
                            Broken open once,
                        </LineReveal>
                        <LineReveal delay={0.1}>
                            through the cracks emerged <span className="text-rose">a colorful sound</span>
                        </LineReveal>
                        <LineReveal delay={0.2}>
                            that will always be heard.
                        </LineReveal>
                    </h2>

                    <div className="mt-10 md:mt-14 space-y-4">
                        <Reveal delay={0.15}>
                            <p className="font-display font-bold text-xl md:text-3xl text-garden">Bold yet humble.</p>
                        </Reveal>
                        <Reveal delay={0.25}>
                            <p className="text-base md:text-xl font-light text-garden/80 max-w-md">A true voice, brave enough to inspire.</p>
                        </Reveal>
                    </div>

                    <Reveal delay={0.3}>
                        <div className="mt-8 md:mt-10">
                            <button
                                type="button"
                                data-testid="about-read-more-toggle"
                                aria-expanded={bioOpen}
                                aria-controls="about-bio-panel"
                                onClick={() => setBioOpen((o) => !o)}
                                className="inline-flex items-center gap-2 text-[11px] md:text-xs uppercase tracking-[0.22em] text-rose hover:text-garden transition-colors duration-200"
                            >
                                {bioOpen ? "Read less" : "Read more"}
                                <ChevronDown
                                    className={`h-4 w-4 transition-transform duration-300 ${bioOpen ? "rotate-180" : ""}`}
                                    aria-hidden="true"
                                />
                            </button>
                            <AnimatePresence initial={false}>
                                {bioOpen && (
                                    <motion.div
                                        id="about-bio-panel"
                                        data-testid="about-read-more-panel"
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.5, ease: EASE }}
                                        className="overflow-hidden"
                                    >
                                        <div className="pt-6 md:pt-8 space-y-4 max-w-2xl text-sm md:text-base font-light leading-relaxed text-garden/75 border-l-2 border-olive/50 pl-5 md:pl-7">
                                            {BIO.map((para, i) => (
                                                <p key={i}>{para}</p>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="mt-12 md:mt-16 pt-10 md:pt-12 border-t border-olive/40 max-w-xl">
                            <p className="font-display font-bold text-2xl md:text-3xl text-garden mb-5">Get the full song free.</p>
                            <EmailCapture id="about" variant="light" />
                        </div>
                    </Reveal>
                </div>

                <div className="md:col-span-5 order-1 md:order-2 relative">
                    <Reveal>
                        <img
                            src="/images/about-1.jpg"
                            alt="Mashi J seated in soft studio light, looking away"
                            loading="lazy"
                            className="w-full aspect-[5/6] object-cover"
                        />
                    </Reveal>
                    <Reveal delay={0.15} className="w-2/3 ml-auto -mt-16 md:-mt-24 relative z-10">
                        <img
                            src="/images/about-2.jpg"
                            alt="Mashi J singing into a vintage microphone, black and white"
                            loading="lazy"
                            className="w-full aspect-[3/4] object-cover"
                        />
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
